import React, { useState, useEffect, memo } from "react";
import { Form } from "react-bootstrap";
import ArrowIconDisabled from "../../../../../assets/img/arrow-filter-solid-disabled.svg";
import ArrowIcon from "../../../../../assets/img/arrow-filter-solid.svg";
import FilterIcon from "../../../../../assets/icons/Icon - FilterDisabled.svg";
import FilterIconOn from "../../../../../assets/icons/Icon - Filter.svg";
import FiltersPanel from "../../../../../components/filter-panel/filter-panel";
import {
  selectTemplates,
  selectSelectedTemplates,
} from "../../../../../redux/templates/template-selector";
import { useSelector, useDispatch } from "react-redux";
import { setSelectedTemplate } from "../../../../../redux/templates/template-actions";
import { selectUser } from "../../../../../redux/user/user-selector";
import PropTypes from "prop-types";
import "./templates-table-header.styles.scss";
import { GetFiltersAPI } from "../../../../../services/api/project-template-api";

const tableHeaderInfo = [
  {
    name: "PROJECT TEMPLATE NAME",
    filterName: "Project Templates",
    containerClassName: "th-template-name-col",
    hasFilter: true,
    hasSort: true,
    filterType: "projectTemplateNamesNew",
    sortBy: "Name",
  },
  {
    name: "DESCRIPTION",
    filterName: "",
    containerClassName: "th-description-col",
    hasFilter: false,
    hasSort: true,
    filterType: "",
    sortBy: "Description",
  },
  {
    name: "DURATION (DAYS)",
    filterName: "",
    containerClassName: "th-duration-col",
    hasFilter: false,
    hasSort: true,
    filterType: "",
    sortBy: "Days",
  },
  {
    name: "ACCESS",
    filterName: "",
    containerClassName: "th-access-col",
    hasFilter: false,
    hasSort: true,
    filterType: "",
    sortBy: "Access",
  },
  {
    name: "ACTIONS",
    filterName: "",
    containerClassName: "th-admin-actions-col",
    hasFilter: false,
    hasSort: false,
    filterType: "",
    sortBy: "",
  },
];

const TemplateTableHeaderNew = ({ changeData, refreshPage, isArchived, setIsArchived }) => {
  const [activeFilterType, setActiveFilterType] = useState("");
  const [activeSortType, setActiveSortType] = useState("");
  const [isDescendingSort, setIsDescendingSort] = useState(false);
  const [selectedFilterRows, setSelectedFilterRows] = useState([]);
  const [filterList, setFilterList] = useState({});

  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const data = useSelector(selectTemplates);
  const selectedRows = useSelector(selectSelectedTemplates);

  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const response = await GetFiltersAPI();
        setFilterList(response.data[0]);
      } catch (error) {
        console.error("Error fetching filters:", error);
      }
    };
    fetchFilters(); // Fetch filters on mount
  }, []);

  useEffect(() => {
    if (isArchived) {
      const fetchFilters = async () => {
        try {
          const response = await GetFiltersAPI();
          const newFilterList = response.data[0];
          setFilterList(newFilterList);
          const updatedFilterList = filterList.length !== newFilterList.length ? newFilterList : filterList; // Ensuring filterList is up-to-date before filtering in case of archived item
          const filterRowsNew = selectedFilterRows.filter(row => 
            isRowInUpdatedFilterList(row, updatedFilterList)
          );
          setSelectedFilterRows(filterRowsNew);
          setIsArchived(false);
          if (filterRowsNew.length === 0) {
            setActiveFilterType("");
          }
        } catch (error) {
          console.error("Error fetching filters:", error);
        }
      };
      fetchFilters(); //Fetch filters when an item from filtered list is archived
    }
  }, [isArchived, filterList, selectedFilterRows, setIsArchived]);

  const isRowInUpdatedFilterList = (row, updatedFilterList) => {
    return updatedFilterList.some(filter => filter.Id === row.Id);
  };

  const onSelectAll = (e) => {
    let tempList = [];
    if (e.target.checked) {
      data.forEach((template) => {
        tempList.push(template.Id);
      });
    }
    dispatch(setSelectedTemplate(tempList));
  };

  const passPayload = (label) => {
    switch (label) {
      case "PROJECT TEMPLATE NAME":
        return filterList;
      default:
        return [];
    }
  };

  const updateFilter = (selectedRows, type) => {
    setSelectedFilterRows(selectedRows);
    setActiveFilterType(type);
  };

  const clearFilter = () => {
    setActiveFilterType("");
    setSelectedFilterRows([]);
    refreshPage();
  };

  const saveFilter = (newFilter) => {
    //Buffer function makes sure we only makes the necessary changes and nothing else
    changeData(newFilter);
  };

  const handleSortClick = (sortType) => {
    //All the sortDesc logic is stored in the search query in UsersTableContainer
    if (activeSortType === sortType && !isDescendingSort) {
      //If sorting by ascending, then turn off on this click
      setActiveSortType("");
      setIsDescendingSort(false);
      changeData(undefined, "name", false);
      return;
    }

    if (activeSortType !== sortType) {
      // If not sorting yet, just set to sorting
      setActiveSortType(sortType);
      setIsDescendingSort(true);
      changeData(undefined, sortType, true);
      return;
    }

    //Now sorting upwards
    setIsDescendingSort(false);
    changeData(undefined, sortType, false);
  };

  return (
    <thead className="template-table-header">
      <tr>
        <th className="th-checkbox-col">
          <Form.Check
            type={"checkbox"}
            disabled={!user?.privileges["Templates"].Delete}
            onClick={(e) => onSelectAll(e)}
            checked={selectedRows.length === data.length && data.length !== 0}
          />
        </th>
        {tableHeaderInfo.map((labelInfo) => {
          return (
            <th className={labelInfo.containerClassName} key={labelInfo.name}>
              <div className="th-container">
                <div className={`th-icon-filter`}>
                  {labelInfo.hasFilter && (
                    <FiltersPanel
                      selected={
                        activeFilterType === labelInfo.filterType
                          ? selectedFilterRows
                          : []
                      }
                      filterIcon={
                        activeFilterType === labelInfo.filterType
                          ? FilterIconOn
                          : FilterIcon
                      }
                      header={labelInfo.filterName}
                      type={labelInfo.filterType}
                      updateSelected={updateFilter}
                      save={saveFilter}
                      clear={clearFilter}
                      change={() => {}}
                      close={() => {}}
                      adminPayload={passPayload(labelInfo.name)}
                    ></FiltersPanel>
                  )}
                </div>
                <div className="th-name">{labelInfo.name}</div>
                {labelInfo.hasSort && (
                  <div className="arrow-holder">
                    <img
                      className="th-icon-up"
                      src={
                        activeSortType === labelInfo.sortBy && !isDescendingSort
                          ? ArrowIcon
                          : ArrowIconDisabled
                      }
                      alt="filter-arrow"
                      onClick={() => handleSortClick(labelInfo.sortBy)}
                    />
                    <img
                      className="th-icon-down"
                      src={
                        activeSortType === labelInfo.sortBy && isDescendingSort
                          ? ArrowIcon
                          : ArrowIconDisabled
                      }
                      alt="filter-arrow"
                      onClick={() => handleSortClick(labelInfo.sortBy)}
                    />
                  </div>
                )}
              </div>
            </th>
          );
        })}
      </tr>
    </thead>
  );
};

TemplateTableHeaderNew.propTypes = {
  refreshPage: PropTypes.func.isRequired,
  changeData: PropTypes.func.isRequired,
  isArchived: PropTypes.bool.isRequired,
  setIsArchived: PropTypes.func.isRequired,
};
export default memo(TemplateTableHeaderNew);
